import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Building2, Filter, Search, ShoppingCart, Star } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

function B2BShopPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = (params.get("search") || "").toLowerCase();
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortBy, setSortBy] = useState("relevance");

  const brand = useMemo(
    () => ({
      accent: "#0EA5E9",
      accent2: "#22C55E",
      bg: "#F7FAFC",
      card: "#FFFFFF",
      text: "#0F172A",
      subtext: "rgba(15, 23, 42, 0.72)",
      stroke: "rgba(2, 6, 23, 0.10)",
    }),
    []
  );

  const products = useMemo(
    () => [
      {
        id: 1,
        title: "Safety Helmet (Bulk Pack)",
        category: "Industrial",
        price: 150,
        moq: 10,
        rating: 4.6,
        vendor: "SafeGear Industrial",
        lead: "2-3 days",
        image:
          "https://images.unsplash.com/photo-1603570419989-83d79d7e9eaa?w=600&h=600&fit=crop",
      },
      {
        id: 2,
        title: "Wi‑Fi Router (Business)",
        category: "Electronics",
        price: 200,
        moq: 5,
        rating: 4.2,
        vendor: "NetWorks Pro",
        lead: "1-2 days",
        image:
          "https://images.unsplash.com/photo-1606055854326-9b6a8843b1f0?w=600&h=600&fit=crop",
      },
      {
        id: 3,
        title: "Packaging Tape (Case)",
        category: "Packaging",
        price: 80,
        moq: 24,
        rating: 4.4,
        vendor: "PackRight Supplies",
        lead: "Same day",
        image:
          "https://images.unsplash.com/photo-1586528116493-92a1f0a4f8b0?w=600&h=600&fit=crop",
      },
      {
        id: 4,
        title: "Ergonomic Chair (Lot)",
        category: "Office",
        price: 250,
        moq: 4,
        rating: 4.0,
        vendor: "OfficeWorks Pro",
        lead: "3-5 days",
        image:
          "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=600&fit=crop",
      },
      {
        id: 5,
        title: "Thermal Labels (Roll)",
        category: "Packaging",
        price: 45,
        moq: 20,
        rating: 4.1,
        vendor: "PackRight Supplies",
        lead: "Same day",
        image:
          "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=600&fit=crop",
      },
    ],
    []
  );

  const categories = useMemo(
    () => ["All", "Industrial", "Electronics", "Packaging", "Office"],
    []
  );

  const filtered = useMemo(() => {
    const base = products.filter((p) => {
      const matchQ =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.vendor.toLowerCase().includes(q);
      const matchCat = activeCategory === "All" || p.category === activeCategory;
      return matchQ && matchCat;
    });

    if (sortBy === "price_low") return [...base].sort((a, b) => a.price - b.price);
    if (sortBy === "price_high") return [...base].sort((a, b) => b.price - a.price);
    if (sortBy === "rating") return [...base].sort((a, b) => b.rating - a.rating);
    return base;
  }, [products, q, activeCategory, sortBy]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.bg, py: 5 }}>
      <Container maxWidth="lg">
        {/* Hero */}
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${brand.stroke}`,
            backgroundImage:
              'linear-gradient(90deg, rgba(14,165,233,0.12), rgba(34,197,94,0.10)), url("https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1400&h=340&fit=crop")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 950, color: brand.text }}>
                  B2B Catalog
                </Typography>
                <Typography variant="body2" sx={{ color: brand.subtext }}>
                  Curated SKUs with MOQ, lead time, and vendor info.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search SKU / vendor / category…"
                  defaultValue={params.get("search") || ""}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      navigate(
                        `/B2BShop?search=${encodeURIComponent(e.target.value || "")}`
                      );
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ pr: 1, display: "flex", alignItems: "center" }}>
                        <Search size={16} />
                      </Box>
                    ),
                  }}
                  sx={{
                    width: { xs: 240, md: 360 },
                    bgcolor: "white",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: brand.stroke,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => navigate("/B2BCart")}
                  startIcon={<ShoppingCart size={16} />}
                  sx={{
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 900,
                    color: "white",
                    background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                  }}
                >
                  Cart
                </Button>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
              {categories.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  onClick={() => setActiveCategory(c)}
                  variant={activeCategory === c ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 900,
                    borderColor: brand.stroke,
                    bgcolor: activeCategory === c ? "rgba(14,165,233,0.10)" : "white",
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          {/* Filters */}
          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Filter size={18} />
                  <Typography sx={{ fontWeight: 950, color: brand.text }}>Filters</Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: brand.subtext, mb: 2 }}>
                  Demo filters UI (wire to API later).
                </Typography>

                <Typography sx={{ fontWeight: 900, color: brand.text, mb: 1 }}>
                  Sort
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="relevance">Relevance</MenuItem>
                  <MenuItem value="rating">Rating</MenuItem>
                  <MenuItem value="price_low">Price: Low → High</MenuItem>
                  <MenuItem value="price_high">Price: High → Low</MenuItem>
                </TextField>

                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 900, color: brand.text, mb: 1 }}>
                    Vendor highlights
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      {
                        name: "PackRight Supplies",
                        img:
                          "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=200&h=200&fit=crop",
                      },
                      {
                        name: "OfficeWorks Pro",
                        img:
                          "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?w=200&h=200&fit=crop",
                      },
                    ].map((v) => (
                      <Box
                        key={v.name}
                        sx={{
                          p: 1.25,
                          borderRadius: 3,
                          border: `1px solid ${brand.stroke}`,
                          bgcolor: "white",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <Avatar variant="rounded" src={v.img} sx={{ width: 40, height: 40 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 950, color: brand.text }} noWrap>
                            {v.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: brand.subtext }} noWrap>
                            Contract vendor
                          </Typography>
                        </Box>
                        <IconButton size="small" sx={{ ml: "auto" }}>
                          <Building2 size={16} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Grid */}
          <Grid item xs={12} md={9}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: brand.subtext }}>
                Showing <b>{filtered.length}</b> items
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate("/B2BRFQ")}
                sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 950, borderColor: brand.stroke, color: brand.text }}
              >
                Request quote for list
              </Button>
            </Stack>

            <Grid container spacing={3}>
              {filtered.map((p) => (
                <Grid item xs={12} sm={6} lg={4} key={p.id}>
                  <Card
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      border: `1px solid ${brand.stroke}`,
                      cursor: "pointer",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                    onClick={() => navigate(`/B2BProduct?id=${p.id}`)}
                  >
                    <CardMedia component="img" height="220" image={p.image} alt={p.title} />
                    <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                      <Typography sx={{ fontWeight: 950, color: brand.text }}>
                        {p.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: brand.subtext }} noWrap>
                        {p.vendor} • {p.category}
                      </Typography>

                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                        <Star size={16} color="#F59E0B" fill="#F59E0B" />
                        <Typography variant="body2" sx={{ fontWeight: 900, color: brand.text }}>
                          {p.rating}
                        </Typography>
                        <Typography variant="caption" sx={{ color: brand.subtext }}>
                          • Lead: {p.lead}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap" }}>
                        <Chip size="small" label={`MOQ ${p.moq}`} />
                        <Chip size="small" label={`From ₹${p.price}`} />
                      </Stack>

                      <Box sx={{ mt: "auto", pt: 2 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          sx={{
                            borderRadius: 3,
                            textTransform: "none",
                            fontWeight: 950,
                            color: "white",
                            background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                          }}
                        >
                          View details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default B2BShopPage;

